import { useState, useEffect, useRef, useCallback } from 'react'
import { publicApi, publicRoomsApi } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Mail, Loader2, ShieldCheck, ArrowLeft, KeyRound } from '@digital-twins/design-system/icons'

export interface EmailGateVerifiedResult {
  organizationAccessGranted?: boolean
}

interface EmailGateFormProps {
  shareToken: string
  onVerified: (result: EmailGateVerifiedResult) => void
  /**
   * Which OTP backend to use. Defaults to 'study' for backward compat with the
   * `/share/:token` flow. Use 'room' for `/share/room/:token`.
   */
  kind?: 'study' | 'room'
}

type Step = 'email' | 'otp'

function getErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message || fallback : fallback
}

export function EmailGateForm({ shareToken, onVerified, kind = 'study' }: EmailGateFormProps) {
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cooldown, setCooldown] = useState(0)
  const otpInputRef = useRef<HTMLInputElement>(null)

  // Pick the right backend per share kind. Both surfaces have identical
  // shapes (sendOtp + verifyEmail) so the rest of the component is unchanged.
  const otpApi = kind === 'room' ? publicRoomsApi : publicApi
  const subjectNoun = kind === 'room' ? 'conversation' : 'report'
  const ctaLabel = kind === 'room' ? 'Unlock conversation' : 'Unlock report'
  const heroDescription =
    kind === 'room'
      ? "Someone shared an exclusive conversation with you. Drop your email and we'll send a quick code to enter."
      : "Someone shared exclusive research insights with you. Drop your email and we'll send a quick code to enter."

  // Cooldown timer for resend
  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  // Auto-focus OTP input when entering step 2
  useEffect(() => {
    if (step !== 'otp') return
    const timer = setTimeout(() => otpInputRef.current?.focus(), 100)
    return () => clearTimeout(timer)
  }, [step])

  const handleSendOtp = useCallback(async () => {
    const trimmed = email.trim()
    if (!trimmed) return

    setLoading(true)
    setError(null)

    try {
      await otpApi.sendOtp(shareToken, trimmed)
      setStep('otp')
      setOtp('')
      setCooldown(60)
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Failed to send code. Please try again.')
      const isNotAuthorized = message.toLowerCase().includes('not authorized')
      setError(isNotAuthorized
        ? `This email doesn\u2019t have access to this ${subjectNoun}.`
        : message)
    } finally {
      setLoading(false)
    }
  }, [email, shareToken, otpApi, subjectNoun])

  const handleVerifyOtp = useCallback(async () => {
    const trimmedOtp = otp.trim()
    if (!trimmedOtp || trimmedOtp.length !== 6) return

    setLoading(true)
    setError(null)

    try {
      const result = await otpApi.verifyEmail(shareToken, email.trim(), trimmedOtp)
      onVerified({
        organizationAccessGranted:
          (result as EmailGateVerifiedResult)?.organizationAccessGranted === true,
      })
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Verification failed. Please try again.'))
    } finally {
      setLoading(false)
    }
  }, [otp, shareToken, email, onVerified, otpApi])

  const handleBack = () => {
    setStep('email')
    setOtp('')
    setError(null)
  }

  if (step === 'email') {
    return (
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-6 py-12 px-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--share-gate-icon-background)_/_0.1)]">
            <ShieldCheck className="h-7 w-7 text-[hsl(var(--share-gate-icon))]" />
          </div>

          <div className="flex flex-col gap-2">
            <Heading level={2} className="font-display text-3xl font-normal">
              You're one step away
            </Heading>
            <Text size="sm" color="muted" className="max-w-xs">
              {heroDescription}
            </Text>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleSendOtp()
            }}
            className="w-full space-y-4"
          >
            <div className="space-y-2 text-left">
              <Label htmlFor="guest-email">Email address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="guest-email"
                  data-testid="guest-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  autoFocus
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <Text size="sm" data-testid="email-gate-error" className="text-destructive text-left">
                {error}
              </Text>
            )}

            <Button
              type="submit"
              variant="cta"
              data-testid="guest-send-code-button"
              className="w-full bg-[hsl(var(--share-action))] text-[hsl(var(--share-action-foreground))] hover:bg-[hsl(var(--share-action)_/_0.9)]"
              disabled={loading || !email.trim()}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending code...
                </>
              ) : (
                'Send me the code'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-md">
      <CardContent className="flex flex-col items-center gap-6 py-12 px-8 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--share-gate-icon-background)_/_0.1)]">
          <KeyRound className="h-7 w-7 text-[hsl(var(--share-gate-icon))]" />
        </div>

        <div className="flex flex-col gap-2">
          <Heading level={2} className="font-display text-3xl font-normal">
            Check your inbox
          </Heading>
          <Text size="sm" color="muted" className="max-w-xs">
            We sent a 6-digit code to <strong className="text-foreground">{email}</strong> — enter it below to unlock the {subjectNoun}.
          </Text>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            handleVerifyOtp()
          }}
          className="w-full space-y-4"
        >
          <div className="space-y-2 text-left">
            <Label htmlFor="otp-code">Verification code</Label>
            <Input
              ref={otpInputRef}
              id="otp-code"
              data-testid="guest-otp-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={otp}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6)
                setOtp(val)
              }}
              className="text-center text-2xl tracking-widest font-mono"
              required
              disabled={loading}
              autoComplete="one-time-code"
            />
          </div>

          {error && (
            <Text size="sm" data-testid="email-gate-error" className="text-destructive text-left">
              {error}
            </Text>
          )}

          <Button
            type="submit"
            variant="cta"
            data-testid="guest-verify-button"
            className="w-full bg-[hsl(var(--share-action))] text-[hsl(var(--share-action-foreground))] hover:bg-[hsl(var(--share-action)_/_0.9)]"
            disabled={loading || otp.length !== 6}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Unlocking...
              </>
            ) : (
              ctaLabel
            )}
          </Button>

          <div className="flex items-center justify-between pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="guest-back-button"
              onClick={handleBack}
              disabled={loading}
            >
              <ArrowLeft className="mr-1 h-3.5 w-3.5" />
              Back
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="guest-resend-code-button"
              disabled={loading || cooldown > 0}
              onClick={handleSendOtp}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
